import { z } from 'zod';

import { UuidSchema } from './common.js';

/**
 * Website "My Bookings" — authenticated traveller's TravelBooking list.
 *
 * Tabs:
 *  - upcoming  — active bookings with travel start today or later
 *  - cancelled — CANCELLED / REFUNDED
 *  - all       — every non-abandoned booking (upcoming + past + cancelled)
 */

export const MyBookingsTabSchema = z.enum(['upcoming', 'cancelled', 'all']);
export type MyBookingsTab = z.infer<typeof MyBookingsTabSchema>;

export const MyBookingsQuerySchema = z.object({
  tab: MyBookingsTabSchema.default('upcoming'),
});
export type MyBookingsQuery = z.infer<typeof MyBookingsQuerySchema>;

export const MyBookingProductSchema = z.enum(['FLIGHT', 'HOTEL']);
export type MyBookingProduct = z.infer<typeof MyBookingProductSchema>;

/** Calendar date as YYYY-MM-DD. */
const CalendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')
  .describe('Calendar date YYYY-MM-DD');

export const MyBookingListItemSchema = z.object({
  id: UuidSchema,
  reference: z.string(),
  product: MyBookingProductSchema,
  status: z.string(),
  travelStartDate: CalendarDateSchema.nullable(),
  travelEndDate: CalendarDateSchema.nullable(),
  /** Short headline, e.g. "DEL → BOM" or hotel name. */
  title: z.string(),
  /** Supporting line, e.g. city / PNR / room nights. */
  subtitle: z.string().nullable(),
  pnr: z.string().nullable(),
  sellPaise: z.string().describe('Minor units as string (BigInt-safe)'),
  currencyCode: z.string(),
  confirmedAt: z.string().datetime().nullable(),
  cancelledAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type MyBookingListItem = z.infer<typeof MyBookingListItemSchema>;

export const MyBookingsListResponseSchema = z.object({
  tab: MyBookingsTabSchema,
  data: z.array(MyBookingListItemSchema),
  counts: z.object({
    upcoming: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative(),
    all: z.number().int().nonnegative(),
  }),
});
export type MyBookingsListResponse = z.infer<typeof MyBookingsListResponseSchema>;
